import React from 'react';
import { Info } from 'lucide-react';
import { FaSpinner, FaEnvelope, FaBell } from 'react-icons/fa';

const EstimateSummaryWithDHL = ({
    clientEstimateSummary,
    dhlEstimation,
    cif,
    totalItemsValue,
    formData,
    showEstimateResults,
    // isHSCodeRelated,
    userCurrency = ""
}) => {
    // Constants for DHL tax calculation
    const specialVATCountries = ['CM', 'HN', 'MD', 'NA', 'SZ', 'EC', 'SC', 'LR', 'IL', 'LB', 'MW', 'GR'];

    const taxConfigs = {
        CM: { rate: 0.175, type: 'VAT', formula: '(17.5%) based on Duty + CIF value' },
        HN: { rate: 0.150, type: 'SALE', formula: '(15%) based on Duty + CIF value' },
        SZ: { rate: 0.150, type: 'VAT', formula: '(15%) based on Total Items Value + Duty' },
        EC: { rate: 0.150, type: 'VAT', formula: '(15%) based on Total Items Value + Freight + Insurance + DUTY + FODINFA + ICE + SAFEGUARD' },
        MD: { rate: 0.200, type: 'VAT', formula: '(20%) based on Duty + Items Value + Freight + Insurance' },
        SC: { rate: 0.150, type: 'VAT', formula: '(15%) based on Duty + Items Value + Freight + Insurance' },
        LR: { rate: 0.100, type: 'GST', formula: '(10%) based on Items Value + Freight + Insurance + DUTY + ECOWAS_TRADE_LEVY' },
        NA: { rate: 0.15, type: 'VAT', formula: '(110% of 15%) based on Items Value' },
        IL: { rate: 0.180, type: 'VAT', formula: '(18%) based on Items Value + Freight + Insurance  + DUTY' },
        LB: { rate: 0.110, type: 'VAT', formula: '(11%) based on Duty + CIF + ADDITIONAL_DUTY' },
        MW: { rate: 0.165, type: 'VAT', formula: '(16.5%) based on Duty + CIF value' },
        GR: { rate: 0.240, type: 'VAT', formula: '(24%) based on Total Items Value + Freight + Insurance + DUTY' },
    };

    // Utility functions
    const calculateSpecialTax = (dutyAmount, CIF, countryCode, totalItemsValue) => {
        const config = taxConfigs[countryCode];
        if (!config) return 0;

        if (countryCode === 'GR') return config.rate * (CIF + dutyAmount);
        if (countryCode === 'LB') {
            const addyFee = dhlEstimation.feeTotals.find(fee => fee.name === 'ADDY')?.value || 0;
            return config.rate * (dutyAmount + CIF + addyFee);
        }
        if (countryCode === 'NA') return (config.rate * totalItemsValue) * 1.1;
        if (countryCode === 'EC') {
            const relevantFees = ['DUTY', 'FODINFA', 'ICE', 'SAFEGUARD'];
            const feesTotal = dhlEstimation.feeTotals
                .filter(fee => relevantFees.includes(fee.name))
                .reduce((sum, fee) => sum + fee.value, 0);
            return config.rate * (totalItemsValue + feesTotal);
        }
        if (countryCode === 'SZ') return config.rate * (totalItemsValue + dutyAmount);
        if (countryCode === 'LR') {
            const ecowasLevy = dhlEstimation.feeTotals.find(fee => fee.name === 'ECOWAS_TRADE_LEVY')?.value || 0;
            return config.rate * (CIF + dutyAmount + ecowasLevy);
        }
        if (countryCode === 'MW') return config.rate * (dutyAmount + CIF);
        return config.rate * (dutyAmount + CIF);
    };

    const getTaxDisplayName = (originalName, countryCode) => {
        if (['SALE', 'GST'].includes(originalName)) return `VAT (${originalName})`;
        return originalName;
    };

    const getFormulaTooltip = (taxName, countryCode) => {
        if (countryCode === 'AU' && taxName === 'IMPROC') return '(1.2%) based on CIF';
        if (countryCode === 'CR' && taxName === 'LAW') return '(1%) based on CIF';
        if (countryCode === 'CI' && taxName === 'EXTRA_TAX') return '(5%) based on CIF with minimum of 300';
        if (countryCode === 'TR' && taxName === 'EXTRA_TAX') return '(5%) based on CIF for HS Codes starting with 8';
        const config = taxConfigs[countryCode];
        if (!config || taxName !== config.type) return '';
        return config.formula;
    };

    if (!showEstimateResults || !clientEstimateSummary) return null;

    const noServices = !clientEstimateSummary.services || clientEstimateSummary.services.length === 0;

    if (noServices /*|| (isHSCodeRelated && !formData.status)*/) {
        return (
            <div className="w-full flex justify-center mb-10">
                <div className="w-full lg:w-[60%] bg-gradient-to-r from-blue-50 to-blue-100 p-6 border-2 rounded-xl border-cus-blue shadow-lg hover:shadow-xl transition-all duration-300">
                    <h3 className="text-xl font-semibold text-cus-blue mb-4 text-center">
                        Estimate In Progress
                    </h3>
                    <div className="flex flex-col items-center space-y-4">
                        <FaSpinner className="animate-spin text-4xl text-cus-blue" />
                        <p className="text-center text-gray-700">
                            Our admin team is crafting your custom estimation. We'll notify you soon!
                        </p>
                        <div className="flex justify-center space-x-4 text-cus-blue">
                            <FaEnvelope className="text-2xl" />
                            <FaBell className="text-2xl" />
                        </div>
                        <p className="text-sm text-center text-gray-600 mb-4">
                            Keep an eye on your email and notifications for updates.
                        </p>
                    </div>
                    {/* {!noServices && (
                        <div className="mt-6 border-t-2 border-blue-200 pt-4">
                            <h4 className="text-lg font-semibold text-cus-blue mb-2 text-center">
                                Estimate Details
                            </h4>
                            {clientEstimateSummary.services.map((service, index) => {
                                const isHSCodeRelated =
                                    service.description.toLowerCase().includes("hs code") ||
                                    service.description.toLowerCase().includes("hs start") ||
                                    service.description.toLowerCase().includes("hs");

                                if (service.description === "Insurance" && formData.insuranceSelected !== "YES") return null;
                                return (
                                    <div
                                        key={index}
                                        className="flex justify-between items-center py-2 border-b border-blue-200 last:border-b-0"
                                    >
                                        <p className="text-[14px] text-gray-700">{service.description}</p>
                                        <p className={`text-[14px] font-bold ${(formData.pending.includes(service.description) && !formData.status) || isNaN(service.value) || isHSCodeRelated ? 'text-yellow-500' : 'text-cus-blue'}`}>
                                            {(formData.pending.includes(service.description) && !formData.status) || isNaN(service.value) || isHSCodeRelated
                                                ? "Pending"
                                                : `${service.value.toFixed(2)} ${userCurrency}`}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    )} */}
                </div>
            </div>
        );
    }

    // Use dhlTaxes from estimateSummary if available, otherwise calculate from dhlEstimation
    let dhlTaxes = clientEstimateSummary.dhlTaxes || [];
    let dhlTaxTotal = dhlTaxes.reduce((sum, tax) => sum + tax.value, 0);

    if (!dhlTaxes.length && dhlEstimation && cif !== undefined) {
        const CIF = cif;
        const specificTaxes = ['DUTY', 'VAT', 'SALE', 'GST'];
        const mainTaxes = {};
        const otherTaxes = [];
        let otherTaxesTotal = 0;
        const countryCode = dhlEstimation.consigneeAddress?.country;
        const itemHsCode = dhlEstimation.customsDetails?.[0]?.hsCode;

        dhlEstimation.feeTotals.forEach((fee) => {
            if (fee.value <= 0) return;

            if (countryCode === 'LB' && fee.name === 'ACD') return;

            const shouldSkipExcise = countryCode === 'MW' && itemHsCode && itemHsCode.startsWith('85');
            if (fee.name === 'EXCISE' && shouldSkipExcise ||
                (countryCode === 'EC' && fee.name === 'SPCT') ||
                (countryCode === 'DO' && fee.name === 'CONS') ||
                (countryCode === 'ET' && fee.name === 'SWL') ||
                (countryCode === 'IL' && fee.name === 'TAMA')) return;

            if (specificTaxes.includes(fee.name)) {
                if (specialVATCountries.includes(countryCode) &&
                    (fee.name === 'VAT' || fee.name === 'SALE' || fee.name === 'GST')) {
                    const dutyAmount = dhlEstimation.feeTotals.find(f => f.name === 'DUTY')?.value || 0;
                    fee.value = calculateSpecialTax(dutyAmount, CIF, countryCode, totalItemsValue);
                }
                if (fee.value > 0) {
                    mainTaxes[fee.name] = {
                        ...fee,
                        displayName: getTaxDisplayName(fee.name, countryCode)
                    };
                }
            } else {
                if (countryCode === 'AU' && fee.name === 'IMPROC') fee.value = CIF * 0.012;
                if (fee.value > 0) {
                    otherTaxes.push(fee);
                    otherTaxesTotal += fee.value;
                }
            }
        });

        if (countryCode === 'QA' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX')) {
            const extraTaxValue = 400;
            if (extraTaxValue > 0) {
                otherTaxes.push({ name: 'EXTRA_TAX', value: extraTaxValue });
                otherTaxesTotal += extraTaxValue;
            }
        }

        if (countryCode === 'TR' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX') &&
            itemHsCode && itemHsCode.startsWith('8')) {
            const extraTaxValue = CIF * 0.05;
            if (extraTaxValue > 0) {
                otherTaxes.push({ name: 'EXTRA_TAX', value: extraTaxValue });
                otherTaxesTotal += extraTaxValue;
            }
        }

        if (countryCode === 'AU' && !otherTaxes.some(tax => tax.name === 'IMPROC')) {
            const improcValue = CIF * 0.012;
            if (improcValue > 0) {
                otherTaxes.push({ name: 'IMPROC', value: improcValue });
                otherTaxesTotal += improcValue;
            }
        }

        if (countryCode === 'KM' && !otherTaxes.some(tax => tax.name === 'ICF')) {
            const icfValue = CIF * 0.025;
            if (icfValue > 0) {
                otherTaxes.push({ name: 'ICF', value: icfValue });
                otherTaxesTotal += icfValue;
            }
        }

        if (countryCode === 'CR' && !otherTaxes.some(tax => tax.name === 'LAW')) {
            const lawValue = CIF * 0.01;
            if (lawValue > 0) {
                otherTaxes.push({ name: 'LAW', value: lawValue });
                otherTaxesTotal += lawValue;
            }
        }

        if (countryCode === 'CI' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX')) {
            const extraTaxValue = Math.max(CIF * 0.05, 300);
            if (extraTaxValue > 0) {
                otherTaxes.push({ name: 'EXTRA_TAX', value: extraTaxValue });
                otherTaxesTotal += extraTaxValue;
            }
        }

        if (countryCode === 'LB' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX')) {
            const extraTaxValue = 350;
            if (extraTaxValue > 0) {
                otherTaxes.push({ name: 'EXTRA_TAX', value: extraTaxValue });
                otherTaxesTotal += extraTaxValue;
            }
        }

        if (countryCode === 'NA' && !mainTaxes['VAT']) {
            const dutyAmount = dhlEstimation.feeTotals.find(f => f.name === 'DUTY')?.value || 0;
            const vatValue = calculateSpecialTax(dutyAmount, CIF, countryCode, totalItemsValue);
            if (vatValue > 0) mainTaxes['VAT'] = { name: 'VAT', value: vatValue, displayName: 'VAT' };
        }

        if (countryCode === 'MW' && !mainTaxes['VAT']) {
            const dutyAmount = dhlEstimation.feeTotals.find(f => f.name === 'DUTY')?.value || 0;
            const vatValue = calculateSpecialTax(dutyAmount, CIF, countryCode, totalItemsValue);
            if (vatValue > 0) mainTaxes['VAT'] = { name: 'VAT', value: vatValue, displayName: 'VAT' };
        }

        specificTaxes.forEach(taxName => {
            if (mainTaxes[taxName] && mainTaxes[taxName].value > 0) {
                dhlTaxes.push({
                    name: mainTaxes[taxName].displayName,
                    value: mainTaxes[taxName].value,
                    tooltip: getFormulaTooltip(taxName, countryCode)
                });
                dhlTaxTotal += mainTaxes[taxName].value;
            }
        });

        if (otherTaxes.length > 0) {
            dhlTaxes.push({
                name: 'Taxes',
                value: otherTaxesTotal,
                tooltip: '',
                details: otherTaxes
            });
            dhlTaxTotal += otherTaxesTotal;
        }
    }

    const clientTotalValue = clientEstimateSummary.services
        .filter(service => service.description !== "Insurance" || formData.insuranceSelected === "YES")
        .reduce((total, service) => {
            if (formData.pending.includes(service.description) && !formData.status || isNaN(service.value)) return total;
            return total + service.value;
        }, 0);

    const grandTotal = clientTotalValue + dhlTaxTotal;

    return (
        <div className="w-full flex justify-center mb-10">
            <div className="w-full lg:w-[40%] bg-gradient-to-r from-blue-50 to-blue-100 p-6 border-2 rounded-xl border-cus-blue shadow-lg hover:shadow-xl transition-all duration-300">
                <h3 className="text-xl font-semibold text-cus-blue mb-4 text-center">
                    Estimate Summary
                </h3>

                {clientEstimateSummary.services.map((service, index) => {
                    if (service.description === "Insurance" && formData.insuranceSelected !== "YES") return null;
                    return (
                        <div
                            key={`client-${index}`}
                            className="flex justify-between items-center py-2 border-b border-blue-200 last:border-b-0"
                        >
                            <p className="text-[14px] text-gray-700">{service.description}</p>
                            <p className={`text-[14px] font-bold ${(formData.pending.includes(service.description) && !formData.status) || isNaN(service.value) ? 'text-yellow-500' : 'text-cus-blue'}`}>
                                {formData.pending.includes(service.description) && !formData.status || isNaN(service.value)
                                    ? "Pending"
                                    : `${service.value.toFixed(2)} ${userCurrency}`}
                            </p>
                        </div>
                    );
                })}

                {dhlTaxes.length > 0 && (
                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-blue-200">
                        <p className="text-[15px] font-medium text-gray-700">Subtotal</p>
                        <p className="text-[15px] font-bold text-cus-blue">
                            {clientTotalValue.toFixed(2)} {userCurrency}
                        </p>
                    </div>
                )}

                {dhlTaxes.length > 0 && (
                    <>
                        <h4 className="text-lg font-semibold text-cus-blue mt-6 mb-3 text-center border-t-2 border-blue-200 pt-4">
                            Duties and Taxes
                        </h4>
                        {dhlTaxes.map((tax, index) => (
                            <div
                                key={`dhl-${index}`}
                                className="flex justify-between items-center py-2 border-b border-blue-200 last:border-b-0"
                            >
                                <div className="flex items-center gap-2">
                                    <p className="text-[14px] text-gray-700">{tax.name}</p>
                                    {tax.tooltip && (
                                        <div className="group relative">
                                            <Info className="h-4 w-4 text-blue-500 cursor-help" />
                                            <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50">
                                                <p className="text-sm">{tax.tooltip}</p>
                                            </div>
                                        </div>
                                    )}
                                    {tax.details && (
                                        <div className="group relative">
                                            <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50">
                                                <h4 className="font-semibold mb-2">Tax Breakdown</h4>
                                                {tax.details.map((item, idx) => (
                                                    <div key={idx} className="flex justify-between text-sm py-1">
                                                        <span>{item.name}:</span>
                                                        <span>${item.value.toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <p className="text-[14px] font-bold text-cus-blue">
                                    {tax.value.toFixed(2)} {userCurrency}
                                </p>
                            </div>
                        ))}
                        {/* Added DHL Values Subtotal */}
                        <div className="flex justify-between items-center mt-2 pt-2 border-t border-blue-200">
                            <p className="text-[15px] font-medium text-gray-700">Subtotal</p>
                            <p className="text-[15px] font-bold text-cus-blue">
                                {dhlTaxTotal.toFixed(2)} {userCurrency}
                            </p>
                        </div>
                    </>
                )}

                <div className="flex justify-between items-center mt-4 pt-3 border-t-2 border-cus-blue">
                    <p className="text-[16px] font-semibold text-gray-800">Total</p>
                    <p className={`text-[18px] font-extrabold ${(formData.pending !== "" && !formData.status) ? 'text-yellow-500' : 'text-cus-blue'}`}>
                        {formData.pending !== "" && !formData.status
                            ? "Pending"
                            : `${grandTotal.toFixed(2)} ${userCurrency}`}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default EstimateSummaryWithDHL;